import * as React from 'react'
import { createPortal } from 'react-dom'
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  UseFormSetValue,
  UseFormRegister,
} from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { ja } from 'date-fns/locale'

import { Button } from '~/atoms/button/Button'
import { InputRadio } from '~/atoms/form/InputRadio'
import { InputTextArea } from '~/atoms/form/InputTextArea'
import { ModalWrapper } from '~/atoms/modal/ModalWrapper'
import { Toast } from '~/atoms/popup/Toast'
import { ProgressStep } from '~/atoms/progess/ProgressStep'
import { StepProgress } from '~/molecules/progress/StepProgress'
import { FormFooterAction } from '~/molecules/form/FormFooterAction'
import { SupportEventStart } from '~/organisms/reserve/SupportEventStart'
import { SupportEventTakeCare } from '~/organisms/reserve/SupportEventTakeCare'
import { SupportEventEating } from '~/organisms/reserve/SupportEventEating'
import { SupportEventExcretion } from '~/organisms/reserve/SupportEventExcretion'
import { SupportEventSleep } from '~/organisms/reserve/SupportEventSleep'
import { SupportEventBathing } from '~/organisms/reserve/SupportEventBathing'
import { SupportEventPlaying } from '~/organisms/reserve/SupportEventPlaying'
import { SupportEventGoingOut } from '~/organisms/reserve/SupportEventGoingOut'
import { SupportEventOther } from '~/organisms/reserve/SupportEventOther'
import { SupportEventTemperature } from '~/organisms/reserve/SupportEventTemperature'
import { SupportEventHandover } from '~/organisms/reserve/SupportEventHandover'
import { SupportEventFinish } from '~/organisms/reserve/SupportEventFinish'
import { SupportEventModal } from '~/organisms/reserve/SupportEventModal'
import { formatDateString } from '@/utils/dateFormat'
import AddIcon from './add.svg?reactCompornt'

import * as Style from './style.css'

export interface IProps {
  isInitialized?: boolean
  isTokyoBsTmpUse?: boolean
  isReportBack?: boolean
  control: Control<ICompleteReportFormStateStep2>
  fields: FieldArrayWithId<
    ICompleteReportFormStateStep2,
    'supportEvents',
    'id'
  >[]
  errors: FieldErrors<ICompleteReportFormStateStep2>
  supportChildren: IChildSummary[]
  lastComment: IReserveComment
  user: IUser
  inputSupportEvents: ISupportEvent[]
  inputSittingReportChildrenMood: string
  inputComment: string
  inputMessageOnReportBack: string
  inputSittingPhotos: IReserveSittingPhoto[]
  minDate: Date
  minReportChildrenMood: number
  showToast?: boolean
  showError?: boolean
  showConfirm?: boolean
  showDeleteConfirm?: boolean
  errorMessages?: string[]
  showSittingPhotos?: boolean
  showSupportEventModal?: boolean
  showPhotoUploadLoading?: boolean
  showPhotoUploadMask?: boolean
  editSittingPhotoEditUrl: string
  setValue: UseFormSetValue<ICompleteReportFormStateStep2>
  register: UseFormRegister<ICompleteReportFormStateStep2>
  submitHandler: () => void
  cancelHandler: () => void
  clickTmpSaveHandler: () => void
  addSupportEventHandler: (supportEventType: ISupportEventType) => void
  openSupportEventHandler: () => void
  closeSupportEventHandler: () => void
  deleteSupportEventHandler: (index: number) => void
  uploadSittingPhotoHandler: (files: File[]) => void
  deleteSittingPhotoHandler: (index: number) => void
  confirmApproveHandler: () => void
  confirmCancelHandler: () => void
  deleteApproveHandler: () => void
  deleteCancelHandler: () => void
}

export const SittingReportStep2 = ({
  isInitialized,
  isTokyoBsTmpUse,
  isReportBack,
  control,
  fields,
  errors,
  user,
  supportChildren,
  lastComment,
  inputSupportEvents,
  inputSittingReportChildrenMood,
  inputComment,
  inputMessageOnReportBack,
  inputSittingPhotos,
  minDate,
  minReportChildrenMood,
  showToast,
  showError,
  showDeleteConfirm,
  showConfirm,
  errorMessages,
  showSupportEventModal,
  showSittingPhotos,
  showPhotoUploadLoading,
  showPhotoUploadMask,
  editSittingPhotoEditUrl,
  register,
  setValue,
  submitHandler,
  cancelHandler,
  clickTmpSaveHandler,
  addSupportEventHandler,
  openSupportEventHandler,
  closeSupportEventHandler,
  deleteSupportEventHandler,
  uploadSittingPhotoHandler,
  deleteSittingPhotoHandler,
  confirmApproveHandler,
  confirmCancelHandler,
  deleteApproveHandler,
  deleteCancelHandler,
}: IProps): JSX.Element => {
  const supportEventFinish = inputSupportEvents.find(
    (supportEvent) => supportEvent.eventType === 'Finish',
  )

  const validator = (file) => {
    return null
  }

  const onDropRejected = () => {
    alert('写真のアップロードに失敗しました。')
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    maxSize: 12 * 1024 * 1024, // 12MB
    validator,
    onDropAccepted: uploadSittingPhotoHandler,
    onDropRejected,
  })

  return (
    <div className={Style.SittingReportStep2}>
      <div className={Style.SittingReportStep2Progress}>
        <StepProgress>
          <ProgressStep isActive={true}>サポート情報</ProgressStep>
          <ProgressStep isActive={true}>レポート情報</ProgressStep>
          <ProgressStep>入力内容確認</ProgressStep>
        </StepProgress>
      </div>
      <h1 className={Style.SittingReportStep2Title}>
        STEP2. レポート情報の入力
      </h1>
      <form onSubmit={submitHandler}>
        {!isReportBack && (
          <>
            <div className={Style.SittingReportStep2FormSection}>
              <div
                id="edit-report"
                className={Style.SittingReportStep2FormSectionTitle}
              >
                サポート日時
              </div>
              <div className={Style.SittingReportStep2FormSectionDescription}>
                <p
                  className={Style.SittingReportStep2FormSectionDescriptionMain}
                >
                  サポートスケジュール
                </p>
                <p
                  className={Style.SittingReportStep2FormSectionDescriptionSub}
                >
                  実施した保育内容を入力してください
                </p>
                <p
                  className={
                    Style.SittingReportStep2FormSectionDescriptionCaution
                  }
                >
                  ※必ず開始と終了は入力してください。
                </p>
              </div>
              <div className={Style.SittingReportStep2FormRow}>
                <div className={Style.SittingReportStep2SupportEvent}>
                  {fields?.map((field, index) => (
                    <div
                      key={field.id}
                      className={Style.SittingReportStep2SupportEventItem}
                    >
                      {field.eventType === 'Start' && (
                        <SupportEventStart
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventStart
                          }
                          supportChildren={supportChildren}
                          errors={errors}
                          register={register}
                          setValue={setValue}
                        />
                      )}
                      {field.eventType === 'TakeCare' && (
                        <SupportEventTakeCare
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventTakeCare
                          }
                          supportChildren={supportChildren}
                          errors={errors}
                          register={register}
                          setValue={setValue}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Eating' && (
                        <SupportEventEating
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventEating
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Excretion' && (
                        <SupportEventExcretion
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventExcretion
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Sleep' && (
                        <SupportEventSleep
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventSleep
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Bathing' && (
                        <SupportEventBathing
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventBathing
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Playing' && (
                        <SupportEventPlaying
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventPlaying
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'GoingOut' && (
                        <SupportEventGoingOut
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventGoingOut
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Other' && (
                        <SupportEventOther
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventOther
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Temperature' && (
                        <SupportEventTemperature
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[
                              index
                            ] as ISupportEventTemperature
                          }
                          errors={errors}
                          register={register}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Handover' && (
                        <SupportEventHandover
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventHandover
                          }
                          supportChildren={supportChildren}
                          errors={errors}
                          register={register}
                          setValue={setValue}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                      {field.eventType === 'Finish' && (
                        <SupportEventFinish
                          control={control}
                          index={index}
                          minDate={minDate}
                          animation={isInitialized}
                          supportEvent={
                            inputSupportEvents[index] as ISupportEventFinish
                          }
                          supportChildren={supportChildren}
                          errors={errors}
                          register={register}
                          setValue={setValue}
                          deleteHandler={deleteSupportEventHandler}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className={Style.SittingReportStep2Add}>
                <button
                  type="button"
                  className={Style.SittingReportStep2AddButton}
                  onClick={openSupportEventHandler}
                >
                  <AddIcon className={Style.SittingReportStep2AddIcon} />
                  <div>
                    <p className={Style.SittingReportStep2AddButtonText}>
                      保育内容を追加する
                    </p>
                    <p className={Style.SittingReportStep2AddButtonTextSub}>
                      終了報告は必ず追加してください
                    </p>
                  </div>
                </button>
              </div>
              <div
                id="edit-child-mod"
                className={Style.SittingReportStep2FormRow}
              >
                <div className={Style.SittingReportStep2FormRowTitle}>
                  <p className={Style.SittingReportStep2FormRowTitleText}>
                    生活や遊び時のお子様の様子
                  </p>
                </div>
                <div className={Style.SittingReportStep2FormInput}>
                  <div>
                    <InputTextArea
                      rows={8}
                      isErrorAlert={!!errors?.sittingReportChildrenMood}
                      {...register('sittingReportChildrenMood', {
                        required: `${minReportChildrenMood}文字以上で入力して下さい`,
                        validate: (text) => {
                          // 改行や空文字を除いた文字数をカウント
                          if (
                            text.replace(/\s+|\r?\n/g, '').length <
                            minReportChildrenMood
                          ) {
                            return `${minReportChildrenMood}文字以上で入力して下さい`
                          }
                        },
                      })}
                    />
                  </div>
                  <div className={Style.SittingReportStep2FormInputError}>
                    <ErrorMessage
                      errors={errors}
                      name={`sittingReportChildrenMood`}
                      render={({ message }) => <p>{message}</p>}
                    />
                  </div>
                  <div className={Style.SittingReportStep2FormInputInfo}>
                    <div
                      className={Style.SittingReportStep2FormInputAppendCount}
                    >
                      {
                        inputSittingReportChildrenMood.replace(/\s+|\r?\n/g, '')
                          .length
                      }
                      /{minReportChildrenMood}文字以上
                    </div>
                    <div className={Style.SittingReportStep2ExampleLabel}>
                      <label
                        className={Style.SittingReportStep2ExampleLabelText}
                        htmlFor="sittingReportChildrenMoodExample"
                      >
                        入力例
                      </label>
                    </div>
                    <div className={Style.SittingReportStep2Example}>
                      <input
                        className={Style.SittingReportStep2ExampleCtrl}
                        id="sittingReportChildrenMoodExample"
                        type="checkbox"
                      />

                      <div className={Style.SittingReportStep2ExamplePopup}>
                        <div
                          className={Style.SittingReportStep2ExamplePopupClose}
                        >
                          <label
                            className={
                              Style.SittingReportStep2ExamplePopupCloseIcon
                            }
                            htmlFor="sittingReportChildrenMoodExample"
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </label>
                        </div>
                        <p className={Style.SittingReportStep2ExampleText}>
                          こんにちは、本日はご依頼いただき誠にありがとうございました。
                          <br />　
                          <br />
                          帰り道、とても寒かったようで「寒〜い！車がいいなー！寒いから早く帰ろう！！」といってスタスタ歩いてくれました。途中から「手が寒い！」とのことで、抱っこしながら私のマフラーの中に手を入れてもらってあたたかく安全に帰宅しました。
                          <br />
                          ご自身でおトイレに行きおしっこされました。
                          <br />
                          お部屋の中で公文の教材を行いました。今日はひらがなを一通り勉強しました。文字を何度も読んで15分以上集中していました。どんどん手先が器用になってきて「ぬ」や「ね」など難しいひらがなもかけるようになってきました。
                          <br />
                          お味噌汁のお豆腐が嬉しかったようで、「おいしい！」と笑顔で召し上がっていました。またお魚を食べるのが上手でびっくりしました！！
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                id="edit-comment"
                className={Style.SittingReportStep2FormRow}
              >
                <div className={Style.SittingReportStep2FormRowTitle}>
                  <p className={Style.SittingReportStep2FormRowTitleText}>
                    連絡事項
                  </p>
                </div>
                <div className={Style.SittingReportStep2FormInput}>
                  <div>
                    <InputTextArea
                      rows={4}
                      isErrorAlert={!!errors?.comment}
                      {...register('comment', {
                        required: '1文字以上で入力して下さい',
                      })}
                    />
                  </div>
                  <div className={Style.SittingReportStep2FormInputError}>
                    <ErrorMessage
                      errors={errors}
                      name={`comment`}
                      render={({ message }) => <p>{message}</p>}
                    />
                  </div>
                  <div className={Style.SittingReportStep2FormInputInfo}>
                    <div
                      className={Style.SittingReportStep2FormInputAppendCount}
                    >
                      {inputComment.length}/1文字以上
                    </div>
                    <div className={Style.SittingReportStep2ExampleLabel}>
                      <label
                        className={Style.SittingReportStep2ExampleLabelText}
                        htmlFor="commentExample"
                      >
                        入力例
                      </label>
                    </div>
                    <div className={Style.SittingReportStep2Example}>
                      <input
                        className={Style.SittingReportStep2ExampleCtrl}
                        id="commentExample"
                        type="checkbox"
                      />

                      <div className={Style.SittingReportStep2ExamplePopup}>
                        <div
                          className={Style.SittingReportStep2ExamplePopupClose}
                        >
                          <label
                            className={
                              Style.SittingReportStep2ExamplePopupCloseIcon
                            }
                            htmlFor="commentExample"
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </label>
                        </div>
                        <p className={Style.SittingReportStep2ExampleText}>
                          保育園より、明日は創作のお時間があるそうなので汚れても良いお洋服でお越しくださいとのことです。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={Style.SittingReportStep2FormInputDescription}>
                    連絡事項がない場合は「特になし」と記入してください。
                  </div>
                </div>
              </div>
              {isTokyoBsTmpUse && (
                <div
                  id="edit-tokyo-bs-tmp-use"
                  className={Style.SittingReportStep2FormRow}
                >
                  <div className={Style.SittingReportStep2FormRowTitle}>
                    <p className={Style.SittingReportStep2FormRowTitleText}>
                      お子様のお預かり状況
                    </p>
                  </div>
                  <div className={Style.SittingReportStep2FormInput}>
                    <ul>
                      <li className={Style.SittingReportStep2FormInputList}>
                        <InputRadio
                          label="お子様1人のみを預かった"
                          value="single_child"
                          {...register('tokyoBsTmpUseReport', {
                            required: '入力必須項目です',
                          })}
                        />
                      </li>
                      <li className={Style.SittingReportStep2FormInputList}>
                        <InputRadio
                          label="未就学児1人と就学児をサポーター1名で預かった"
                          value="mixed_children"
                          {...register('tokyoBsTmpUseReport', {
                            required: '入力必須項目です',
                          })}
                        />
                      </li>
                      <li className={Style.SittingReportStep2FormInputList}>
                        <InputRadio
                          label="複数の未就学児を保護者と一緒に共同保育した"
                          value="group_care"
                          {...register('tokyoBsTmpUseReport', {
                            required: '入力必須項目です',
                          })}
                        />
                      </li>
                      <li className={Style.SittingReportStep2FormInputList}>
                        <InputRadio
                          label="複数の未就学児を預かったが共同保育ではなかった"
                          value="non_group_care"
                          {...register('tokyoBsTmpUseReport', {
                            required: '入力必須項目です',
                          })}
                        />
                      </li>
                      <li className={Style.SittingReportStep2FormInputList}>
                        <InputRadio
                          label="複数の就学児童のみをサポーター1名で預かった"
                          value="multiple_schooled_children"
                          {...register('tokyoBsTmpUseReport', {
                            required: '入力必須項目です',
                          })}
                        />
                      </li>
                    </ul>
                    <div className={Style.SittingReportStep2FormInputError}>
                      <ErrorMessage
                        errors={errors}
                        name={`tokyoBsTmpUseReport`}
                        render={({ message }) => <p>{message}</p>}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {isReportBack && (
          <>
            <div className={Style.SittingReportStep2FormSection}>
              <div className={Style.SittingReportStep2FormRow}>
                <div className={Style.SittingReportStep2FormRowTitle}>
                  <p className={Style.SittingReportStep2FormRowTitleText}>
                    連絡事項
                  </p>
                </div>
                <div className={Style.SittingReportStep2FormInput}>
                  <div>
                    <InputTextArea
                      rows={4}
                      isErrorAlert={!!errors?.messageOnReportBack}
                      {...register('messageOnReportBack', {
                        required: '1文字以上で入力して下さい',
                      })}
                    />
                  </div>
                  <div className={Style.SittingReportStep2FormInputError}>
                    <ErrorMessage
                      errors={errors}
                      name={`messageOnReportBack`}
                      render={({ message }) => <p>{message}</p>}
                    />
                  </div>
                  <div className={Style.SittingReportStep2FormInputInfo}>
                    <div
                      className={Style.SittingReportStep2FormInputAppendCount}
                    >
                      {inputMessageOnReportBack.length}/1文字以上
                    </div>
                    <div className={Style.SittingReportStep2ExampleLabel}>
                      <label
                        className={Style.SittingReportStep2ExampleLabelText}
                        htmlFor="messageOnReportBackExample"
                      >
                        入力例
                      </label>
                    </div>
                    <div className={Style.SittingReportStep2Example}>
                      <input
                        className={Style.SittingReportStep2ExampleCtrl}
                        id="messageOnReportBackExample"
                        type="checkbox"
                      />

                      <div className={Style.SittingReportStep2ExamplePopup}>
                        <div
                          className={Style.SittingReportStep2ExamplePopupClose}
                        >
                          <label
                            className={
                              Style.SittingReportStep2ExamplePopupCloseIcon
                            }
                            htmlFor="messageOnReportBackExample"
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </label>
                        </div>
                        <p className={Style.SittingReportStep2ExampleText}>
                          差し戻しいただきましたサポートスケジュールを再提出いたします。
                          <br />
                          12:00 サポート開始
                          <br />
                          引き継ぎ者：お父様、引き継ぎ場所： 自宅
                          <br />
                          12:30 遊び
                          <br />
                          室内遊びをしました。
                          <br />
                          12:30 おむつ交換
                          <br />
                          13:00 就寝
                          <br />
                          14:00 起床
                          <br />
                          14:30 サポート終了
                          <br />
                          引き継ぎ者：お母様、引き継ぎ場所： 自宅
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={Style.SittingReportStep2FormInputDescription}>
                    ■前回送信した完了報告の修正は、［連絡事項（欄）］に修正内容を追記してください。
                    <br />
                    前回記載をした連絡事項を消さないようご注意ください。
                  </div>
                </div>
              </div>
            </div>
            <div className={Style.SittingReportStep2FormSection}>
              <div className={Style.SittingReportStep2FormSectionTitle}>
                コメント
              </div>
              <div className={Style.SittingReportStep2FormRow}>
                <div className={Style.SittingReportStep2FormInput}>
                  <ul className={Style.SittingReportStep2ReportBackComments}>
                    <li className={Style.SittingReportStep2ReportBackComment}>
                      <div
                        className={
                          Style.SittingReportStep2ReportBackCommentInfo
                        }
                      >
                        <img
                          className={
                            Style.SittingReportStep2ReportBackCommentThumbnail
                          }
                          src={lastComment.user.image.middle_thumb.url}
                        />
                        <div
                          className={
                            Style.SittingReportStep2ReportBackCommentStatus
                          }
                        >
                          {lastComment.status_str}
                        </div>
                        <div
                          className={
                            Style.SittingReportStep2ReportBackCommentDate
                          }
                        >
                          {formatDateString(
                            lastComment.created_at,
                            'MM月dd日(E) hh:mm',
                            ja,
                          )}
                        </div>
                      </div>
                      <div
                        className={
                          Style.SittingReportStep2ReportBackCommentContent
                        }
                      >
                        {lastComment.comment}
                      </div>
                    </li>
                    <li className={Style.SittingReportStep2ReportBackComment}>
                      <div
                        className={
                          Style.SittingReportStep2ReportBackCommentInfo
                        }
                      >
                        <img
                          className={
                            Style.SittingReportStep2ReportBackCommentThumbnail
                          }
                          src={user.image.middle_thumb.url}
                        />
                        <div
                          className={
                            Style.SittingReportStep2ReportBackCommentStatus
                          }
                        >
                          再報告
                        </div>
                      </div>
                      <div
                        className={
                          Style.SittingReportStep2ReportBackCommentContent
                        }
                      >
                        <div>
                          <InputTextArea
                            rows={4}
                            isErrorAlert={!!errors?.comment}
                            {...register('comment', {
                              required: '4000文字以内で入力して下さい',
                              maxLength: {
                                value: 4000,
                                message: '4000文字以内で入力して下さい',
                              },
                            })}
                          />
                        </div>
                        <div className={Style.SittingReportStep2FormInputError}>
                          <ErrorMessage
                            errors={errors}
                            name={`comment`}
                            render={({ message }) => <p>{message}</p>}
                          />
                        </div>
                        <div className={Style.SittingReportStep2FormInputInfo}>
                          <div
                            className={
                              Style.SittingReportStep2FormInputAppendCount
                            }
                          >
                            残り {4000 - inputComment.length} 文字
                          </div>
                        </div>
                        <div
                          className={
                            Style.SittingReportStep2FormInputDescription
                          }
                        >
                          コメントは一時保存されませんのでご注意ください
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
        {showSittingPhotos && (
          <div className={Style.SittingReportStep2FormSection}>
            <div className={Style.SittingReportStep2FormSectionTitle}>
              サポート中の写真
            </div>
            <div className={Style.SittingReportStep2SittingPhoto}>
              {showPhotoUploadMask && (
                <div className={Style.SittingReportStep2SittingPhotoMask}></div>
              )}
              <div className={Style.SittingReportStep2SittingPhotoUpload}>
                {showPhotoUploadLoading && (
                  <div
                    className={
                      Style.SittingReportStep2SittingPhotoUploadLoading
                    }
                  >
                    <AnimatePresence>
                      <motion.div
                        animate={{
                          transform: ['rotate(0deg)', 'rotate(360deg)'],
                        }}
                        transition={{
                          duration: 2.5,
                          ease: 'linear',
                          repeat: Infinity,
                        }}
                      >
                        <FontAwesomeIcon
                          className={
                            Style.SittingReportStep2SittingPhotoUploadLoadingIcon
                          }
                          icon={faSpinner}
                        />
                      </motion.div>
                    </AnimatePresence>
                    <p
                      className={
                        Style.SittingReportStep2SittingPhotoUploadLoadingMessage
                      }
                    >
                      処理中
                    </p>
                  </div>
                )}
                <div
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                  {...getRootProps()}
                  className={Style.SittingReportStep2SittingPhotoUploadForm}
                >
                  <input
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    {...getInputProps()}
                  />
                  <div
                    className={
                      Style.SittingReportStep2SittingPhotoUploadFormContent
                    }
                  >
                    <div
                      className={
                        Style.SittingReportStep2SittingPhotoUploadFormMessage
                      }
                    >
                      ここに写真をドラッグ＆ドロップしてください
                    </div>
                    <div
                      className={
                        Style.SittingReportStep2SittingPhotoUploadFormSelect
                      }
                    >
                      <img
                        className={
                          Style.SittingReportStep2SittingPhotoUploadFormSelectImage
                        }
                        src="/img/reserves/icons/ic_add_photo.png"
                      />
                      写真を選択する
                    </div>
                    <div
                      className={
                        Style.SittingReportStep2SittingPhotoUploadFormRestriction
                      }
                    >
                      最大アップロードサイズ：12MB
                      <br />
                      20枚まで
                    </div>
                  </div>
                </div>
                <div className={Style.SittingReportStep2SittingPhotoNotice}>
                  お子様が写っている写真をアップロードしてください。
                  <div
                    className={Style.SittingReportStep2SittingPhotoNoticeSub}
                  >
                    (20枚まで)
                  </div>
                </div>
              </div>
              {inputSittingPhotos?.length > 0 && (
                <div>
                  <ul className={Style.SittingReportStep2SittingPhotos}>
                    {inputSittingPhotos.map((sittingPhoto, index) => (
                      <li
                        className={Style.SittingReportStep2SittingPhotoItem}
                        key={`sitting-photo-${index}`}
                      >
                        <div
                          className={
                            Style.SittingReportStep2SittingPhotoItemWrapper
                          }
                        >
                          <div
                            className={
                              Style.SittingReportStep2SittingPhotoItemImage
                            }
                            style={{
                              backgroundImage: `url(${sittingPhoto.url})`,
                            }}
                          >
                            <img
                              className={
                                Style.SittingReportStep2SittingPhotoItemDelete
                              }
                              onClick={() => deleteSittingPhotoHandler(index)}
                              src="/img/reserves/icons/ic_delete_photo.png"
                            />
                          </div>
                        </div>
                        {sittingPhoto.caption && (
                          <div
                            className={
                              Style.SittingReportStep2SittingPhotoItemCaption
                            }
                          >
                            <a
                              className={
                                Style.SittingReportStep2SittingPhotoItemCaptionLink
                              }
                              href={sittingPhoto.editUrl}
                            >
                              <img
                                className={
                                  Style.SittingReportStep2SittingPhotoItemCaptionIcon
                                }
                                src="/img/reserves/icons/ic_edit_caption.png"
                              />
                              <span
                                className={
                                  Style.SittingReportStep2SittingPhotoItemCaptionLinkText
                                }
                              >
                                {sittingPhoto.caption}
                              </span>
                            </a>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div
                    className={Style.SittingReportStep2SittingPhotoEditCaption}
                  >
                    <a
                      className={
                        Style.SittingReportStep2SittingPhotoEditCaptionLink
                      }
                      href={editSittingPhotoEditUrl}
                    >
                      キャプションを入力する
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <FormFooterAction
          cancelHandler={cancelHandler}
          clickTmpSaveHandler={clickTmpSaveHandler}
        />
      </form>
      {isInitialized &&
        createPortal(
          <>
            <Toast
              visible={showToast}
              title="一時保存しました"
              color="notice"
            ></Toast>
            <Toast
              visible={showError}
              title="保存できませんでした"
              color="alert"
            >
              {errorMessages?.map((message, index) => (
                <p key={`error-message-${index}`}>・{message}</p>
              ))}
            </Toast>
            <ModalWrapper
              mobileAnimation="slideUp"
              isOpen={showSupportEventModal}
            >
              <div className={Style.SupportEventModal}>
                <SupportEventModal
                  disabledFinish={!!supportEventFinish}
                  clickHandler={addSupportEventHandler}
                  closeHanlder={closeSupportEventHandler}
                />
              </div>
            </ModalWrapper>
            <ModalWrapper
              animation="fadeIn"
              mobileAnimation="fadeIn"
              isOpen={showConfirm}
            >
              <div className={Style.SittingReportStep2Confirm}>
                <div className={Style.SittingReportStep2ConfirmContent}>
                  <p className={Style.SittingReportStep2ConfirmMessage}>
                    変更を破棄しますか？
                  </p>
                  <ul className={Style.SittingReportStep2ConfirmActions}>
                    <li>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={confirmCancelHandler}
                      >
                        キャンセル
                      </Button>
                    </li>
                    <li>
                      <Button
                        type="button"
                        color="primary"
                        onClick={confirmApproveHandler}
                      >
                        破棄する
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </ModalWrapper>
            <ModalWrapper
              animation="fadeIn"
              mobileAnimation="fadeIn"
              isOpen={showDeleteConfirm}
            >
              <div className={Style.SittingReportStep2Confirm}>
                <div className={Style.SittingReportStep2ConfirmContent}>
                  <p className={Style.SittingReportStep2ConfirmMessage}>
                    本当に削除しますか？
                  </p>
                  <ul className={Style.SittingReportStep2ConfirmActions}>
                    <li>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={deleteCancelHandler}
                      >
                        キャンセル
                      </Button>
                    </li>
                    <li>
                      <Button
                        type="button"
                        color="primary"
                        onClick={deleteApproveHandler}
                      >
                        削除する
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </ModalWrapper>
          </>,
          document.body,
        )}
    </div>
  )
}
